import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  reopenResolvedTicket,
  takeOverTicket,
  ticketFilterOptionsQuery,
  transferTicket,
  type TicketAdvance as Advance,
  type TicketFormName,
} from '../api/easeedesk'
import { toast } from '../ui/notify'
import { TicketActionForm } from './TicketActionForm'

/**
 * Ce se deschide sub butonul "Advance" - `Easeedesk::changeTicketStatus()`.
 *
 * Butonul acela nu duce totdeauna la formularul de avansare. Portalul trece
 * intai prin `checkTicket()`, care pune in locul lui, dupa caz, cu totul alt
 * panou. Serverul spune care (`advance.kind` din `TicketActions::page()`); aici
 * se deseneaza.
 *
 * PRELUAREA e cea care se vede cel mai des, fiindca e starea oricarui tichet pe
 * care omul nu si l-a luat inca: `ticket/actions/takeover.php`, cu cele doua
 * jumatati ale ei - tichet neatribuit (dunga albastra, Takeover si Transfer to)
 * si tichet al altcuiva (dunga rosie cu numele lui, si un singur buton, care
 * intreaba inainte).
 */
export function TicketAdvancePanel({ id, advance }: { id: number; advance: Advance }) {
  if (advance.kind === 'takeover') {
    return <Takeover id={id} assignee={advance.assignee} />
  }

  /*
   * Trei din celelalte cinci sunt formulare, si se deschid: pasul urmator
   * (`FormGenerator`), scoaterea din escaladare (`RemoveEscalationForm`) si
   * reluarea planificarii (`PlanningForms::resume`). Serverul spune care.
   */
  const opens = FORM_OF[advance.kind]
  if (opens) return <TicketActionForm id={id} form={opens} />

  if (advance.kind === 'reopen') return <Reopen id={id} />

  /*
   * Ramane tichetul inchis. Se spune ce urmeaza, nu se trimite nimeni
   * nicaieri: portalul vechi nu mai exista pe server, deci o legatura catre el
   * ar fi o usa care da in zid.
   */
  return (
    <div className="alert alert-info" role="alert">
      <i className="fas fa-info-circle" aria-hidden="true"></i> {WAITING[advance.kind]}
    </div>
  )
}

/** Ce panou tine locul formularului de avansare - `Easeedesk::checkTicket()`. */
const FORM_OF: Record<string, TicketFormName | undefined> = {
  escalated: 'unescalate',
  planned: 'resume',
  form: 'advance',
}

const WAITING: Record<string, string> = {
  'new-ticket': 'This ticket is closed. Nothing can be advanced on it any more.',
}

/**
 * Redeschiderea unui tichet rezolvat - `ticket/actions/reopen_ticket.php` cu
 * `ReopenTicketFormBuilder` (easeedesk.js) si `ReopenTicketForm::reopen()`.
 *
 * Formularul vechi avea un singur camp, "Comments" (textarea, obligatoriu), si
 * butonul "Reopen ticket". Trimitea `ticket_id` + `comments` la
 * `resumeResolvedTicket`; aici merge la `POST /easeedesk/tickets/{id}/reopen`,
 * care cere aceleasi drepturi ca "Advance" si trece tichetul prin gardul firmei.
 *
 * Dupa raspuns fisa se reciteste - tichetul nu mai e rezolvat, deci si panoul
 * de sub "Advance" e altul.
 */
function Reopen({ id }: { id: number }) {
  const client = useQueryClient()
  const [comments, setComments] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  function submit(ev: FormEvent): void {
    ev.preventDefault()
    const text = comments.trim()
    if (text === '') {
      setErrors(['The Comments field is required.'])
      return
    }

    setErrors([])
    setBusy(true)
    reopenResolvedTicket(id, text)
      .then(() => {
        toast(`Ticket #${id} was reopened.`, 'Success', 'success')
        setComments('')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
        void client.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
      })
      .catch((err) => setErrors([err instanceof Error ? err.message : 'Error reopening the ticket']))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <h4>Reopen ticket</h4>

      <hr className="dotted" />

      <form className="form-horizontal reopenTicketForm" onSubmit={submit} noValidate>
        <div className="form-errors">
          {errors.map((one) => (
            <div key={one} className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {one}
            </div>
          ))}
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" htmlFor={`reopen-comments-${id}`}>
            Comments <span className="required">*</span>
          </label>
          <div className="col-sm-9">
            <textarea
              id={`reopen-comments-${id}`}
              name="comments"
              className="form-control"
              rows={4}
              required
              value={comments}
              onChange={(ev) => setComments(ev.target.value)}
            />
          </div>
        </div>

        <div className="form-group aright">
          <div className="col-sm-12">
            <button type="submit" name="submit_form" className="btn btn-primary" disabled={busy}>
              <i className="fas fa-check" aria-hidden="true"></i> Reopen ticket
            </button>
          </div>
        </div>
      </form>
    </>
  )
}

/** `ticket/actions/takeover.php`, cele doua ramuri ale ei. */
function Takeover({ id, assignee }: { id: number; assignee: { id: number; name: string } }) {
  const client = useQueryClient()
  const [busy, setBusy] = useState(false)

  function done(): void {
    /* Fisa se reciteste: randul de sus, panoul de actiuni si jurnalul poarta
       toate operatorul. Aceeasi reimprospatare pe care o face si portalul,
       unde butonul reincarca panoul dupa raspuns. */
    void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
    void client.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
  }

  function take(force: boolean): void {
    setBusy(true)
    takeOverTicket(id, force)
      .then((answer) => {
        if (answer.confirm === 1) {
          /* Nu se poate ajunge aici din ramura de jos - acolo `force` e deja
             pornit - dar raspunsul exista si nu se inghite in tacere. */
          toast('This ticket is already assigned to someone else.', 'Error', 'error')
          return
        }
        toast(`Ticket #${id} is yours.`, 'Success', 'success')
        done()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Error taking over', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  if (assignee.id > 0) {
    return (
      <>
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-info-circle"></i> This ticket is already assigned to <strong>{assignee.name}</strong>. Are you sure you want to take it
          over ?
        </div>

        <div className="form-group row">
          <div className="col-md-12">
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => take(true)}>
              <i className="fas fa-sync-alt" aria-hidden="true"></i> Takeover
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle"></i> You are not responsible for this ticket. If you want to make changes on it, you must take it over first.
      </div>

      <table>
        <tbody>
          <tr>
            <td style={{ display: 'inline-block' }}>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => take(false)}>
                <i className="fas fa-sync-alt" aria-hidden="true"></i> Takeover
              </button>
            </td>

            <td style={{ display: 'inline-block' }}>
              <TransferTo id={id} onDone={done} />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

/**
 * "Transfer to", butonul de langa Takeover.
 *
 * In portal e un x-editable: `<a class="btn btn-theme-inverse">` care, la
 * apasare, se preface intr-o lista de echipe. Aceeasi purtare - butonul ramane
 * un buton pana e apasat, si abia atunci apare lista - fiindca panoul e ingust
 * si un `<select>` desenat de la inceput ar fi luat locul lui Takeover.
 */
function TransferTo({ id, onDone }: { id: number; onDone(): void }) {
  const [open, setOpen] = useState(false)
  const [group, setGroup] = useState('')
  const [busy, setBusy] = useState(false)
  const options = useQuery({ ...ticketFilterOptionsQuery, enabled: open })

  if (!open) {
    return (
      <a
        className="btn btn-theme-inverse"
        href="#"
        style={{ borderBottom: '1px solid transparent' }}
        onClick={(ev) => {
          ev.preventDefault()
          setOpen(true)
        }}
      >
        <i className="fas fa-people-arrows"></i> Transfer to
      </a>
    )
  }

  function save(): void {
    const to = Number(group)
    if (!to) return

    setBusy(true)
    transferTicket(id, to)
      .then(() => {
        toast(`Ticket #${id} moved to another group.`, 'Success', 'success')
        setOpen(false)
        onDone()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Error transferring', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <span className="tk-transfer">
      <select className="form-control input-sm" value={group} onChange={(ev) => setGroup(ev.target.value)} aria-label="Transfer to group">
        <option value="">Required</option>
        {(options.data?.groups ?? []).map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
      <button type="button" className="btn btn-theme-inverse btn-sm" disabled={busy || group === ''} onClick={save}>
        <i className="fas fa-check"></i>
      </button>
      <button type="button" className="btn btn-default btn-sm" onClick={() => setOpen(false)}>
        <i className="fas fa-times"></i>
      </button>
    </span>
  )
}
